import { StandAloneContent, StandAloneHeader } from 'Components/EventStepper/components';
import TableView from 'Components/EventStepper/components/TableView';
import { ViewSelectorStandAlone } from 'Components/EventStepper/components/ViewSelector';
import { fm } from 'Components/FormatMessage';
import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';
import { isPartialDate, nowPartialDate, sortPartialDate } from 'neuro-utils';
import * as React from 'react';
import { StyledControls } from './components';

const EventViewer = ({
  name,
  formData,
  dateFieldName,
  content,
  tableContent,
  tableHeaders,
  header,
  dateDefault,
  dateHook,
  enabledDatePickerDates,
  noEventsTextHeader,
  activeView,
  setActiveView,
  tableContentFilter,
}: IOwnProps): React.JSX.Element => {
  const [dateFilter, setDateFilter] = React.useState<PartialDate | undefined>(undefined);
  const dateField = typeof dateFieldName === 'string' ? dateFieldName : 'date';

  const events: Array<any> =
    typeof formData.document === 'object' &&
    name in formData.document &&
    Array.isArray(formData.document[name as keyof typeof formData.document])
      ? formData.document[name as keyof typeof formData.document]
      : [];

  React.useEffect(() => {
    const latestEvent = events.toSorted((a, b) => sortPartialDate(b[dateField], a[dateField]))[0];
    const latestDate = latestEvent?.[dateField];
    if (!dateFilter) setDateFilter(dateDefault ?? latestDate ?? nowPartialDate());
  }, []);

  const eventsForDate = events.filter(e => sortPartialDate(e[dateField], dateFilter) === 0);

  const [eventIndex, setEventIndex] = React.useState<number>(0);

  return (
    <>
      <FormRow title={undefined} headerWidth={activeView === 'table' ? 12 : undefined}>
        {tableContent && <ViewSelectorStandAlone activeView={activeView} setActiveView={setActiveView} />}
        {events.length === 0 ? (
          fm(noEventsTextHeader ?? 'general.noEvents')
        ) : (
          <React.Fragment>
            {activeView === 'list' && (
              <StandAloneHeader style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ marginLeft: '8px' }}>
                  <InputHandler
                    type="PartialDate"
                    name="dateFilter"
                    editing={true}
                    formData={{
                      document: { dateFilter },
                      onChange: values => {
                        const name = Object.keys(values)[0];
                        if (!name) return;
                        const value = values[name];
                        if (isPartialDate(value)) {
                          setEventIndex(0);
                          setDateFilter(value);
                        }
                      },
                    }}
                    isNotCancellable
                    dateDefault={dateDefault ?? 'now'}
                    dateHook={dateHook}
                    disableDateSelection
                    enabledDatePickerDates={enabledDatePickerDates}
                  />
                </div>
                <div>
                  {eventsForDate.length > 1 && (
                    <StyledControls
                      eventIndex={eventIndex}
                      events={eventsForDate}
                      changeEventIndex={change => () => setEventIndex(change)}
                    />
                  )}
                </div>
              </StandAloneHeader>
            )}

            {/** Event view(s) */}
            {
              {
                list: (
                  <StandAloneContent editing={false}>
                    {eventsForDate.length > 0 ? (
                      content(eventsForDate[eventIndex])
                    ) : (
                      <span>{fm('general.noEventsForSelectedDate')}</span>
                    )}
                  </StandAloneContent>
                ),
                table: (
                  <TableView
                    normalizedProps={{
                      name,
                      formData,
                      stepLabelText: tableHeaders ?? (() => ''),
                      stepContent: content,
                      addNewTextHeader: '',
                      addNewTextButton: '',
                      previousEventsTextHeader: '',
                      editingElements: () => <></>,
                      tableContent,
                      tableContentFilter,
                    }}
                    outsideEvents={events}
                    eventLimit={3}
                    tableControlsOverHeader
                  />
                ),
              }[activeView]
            }
          </React.Fragment>
        )}
      </FormRow>
    </>
  );
};

interface IOwnProps {
  /** Name of the event array in formData */
  name: string;
  /** Form data */
  formData: IFormData<any>;
  /** Name for the date field events are sorted by */
  dateFieldName?: string;
  /** Content shown - use StepperHeaderValuePairs */
  content: (d: any) => React.JSX.Element; // StepperHeaderValuePairs
  /** Content to be shown in table format */
  tableContent?: (d: any) => React.JSX.Element;
  /** Headers for table documents, date/etc */
  tableHeaders?: (d: any) => React.JSX.Element;
  /** Locale id for FormRow header */
  header?: string;
  dateDefault?: IPartialDate['dateDefault'];
  dateHook?: IPartialDate['dateHook'];
  /** Enabled dates for date picker */
  enabledDatePickerDates?: IPartialDate['enabledDatePickerDates'];
  noEventsTextHeader?: string;

  activeView: 'list' | 'table';
  setActiveView: (view: 'list' | 'table') => void;

  tableContentFilter?: {
    /** Input component used to set the filter - placed in table header bar */
    input?: React.JSX.Element;
    /** Filter function that returns the filtered events */
    eventFilter?: (d: any) => any[];
    width: number;
  };
}

export default EventViewer;
